import { markRaw, type Component } from 'vue';
import { GroupDef } from './GroupDef';
import type { ValidatorDef } from './ValidatorDef';
import type { IGridColumnCssRule } from '@/components/Grid/Classes';
import type { IGridAggregate } from '@/components/Grid/Classes/IGridAggregate';
import { TabDefinition } from '../Tabs';
import type { SortDirection } from '../generated';
import { CollapsableDef } from './CollapsableDef';
import type { SuportedEditorTypes } from '../Valueparsers/ComponentValueUtils';

export type FieldDefinedFor = 'Detail' | 'List' | 'Both';

export class FieldDef<T, TField = any> {
    public Group: GroupDef | null = null;

    /**
     * The tab this field belong to. This is only used in the detail component for autogenerating the tabs
     */
    public Tab: TabDefinition | null = null;

    public Collapsable: CollapsableDef | null = null;

    public Name!: string;
    public TranslationKey: string | null = null;
    public FieldName!: string;
    public DefinedFor: FieldDefinedFor = 'Both';

    /**
     * If the field is editable
     */
    public Editable: boolean | ((row: TField) => boolean) = true;

    /**
     * If the column can be filtered
     */
    public Filterable = false;
    public Sortable?: boolean = undefined;
    public DataType?: string;
    public MinWidth?: number = undefined;
    public MaxWidth?: number = undefined;
    public Tooltip: string | null = null;

    public DetailEditor!: Component<{ modelValue: TField }>;
    public CellRenderer: (Component<{ value: TField }> & SuportedEditorTypes) | null = null; // NULL means use only textvalue
    public CellEditor!: Component<{ modelValue: TField }> & SuportedEditorTypes;

    /**
     * Additional properties that can be used by the renderer Example for InputDateTime: {format: 'YYYY-MM-DD HH:mm'}
     * */
    public DetailEditorProps: any = {};

    /**
     * Props for the Cell renderer in grid
     */
    public CellRendererProps: any = {};

    /**
     * Props for the Cell editor in grid
     */
    public CellEditorProps: any = {};

    /**
     * Can be used to generate the props when we have the row available
     * This is never filled by the backend, only applicable if you overwrite it in the frontend
     */
    public DetailEditorPropsGetter?: (obj: T) => any;

    /**
     * Can be used to generate the props when we have the row available
     * This is never filled by the backend, only applicable if you overwrite it in the frontend
     */
    public CellRendererPropsGetter?: (obj: T) => any;

    /**
     * Can be used to generate the props when we have the row available
     * This is never filled by the backend, only applicable if you overwrite it in the frontend
     */
    public CellEditorPropsGetter?: (obj: T) => any;

    public HeaderFilterValueGetter?: (values: T) => string;

    public CssClasses: string | null = null;

    public Validator: ValidatorDef | null = null;

    // The sort direction of the column
    public SortDirection: SortDirection | undefined = undefined;

    // The class rules to apply to the cell
    public cellClassRules?: IGridColumnCssRule<T, TField>[];

    public value!: (obj: T) => TField;
    public setValue!: (obj: T, value: TField) => void;

    // How to aggreagate the values of the column. This will be shown in the footer of the column
    public Aggregate?: IGridAggregate;

    public ExportValueFormatter?: (obj: T) => any;

    public IsDefinedFor(type: FieldDefinedFor) {
        return ['Both', type].includes(this.DefinedFor);
    }
}

/**
 * Will help us build field definitions for detail views
 * This only support the properties needed for detail views
 */
export class DetailFieldDefBuilder<T, TField = any> {
    private detailEditor: Component<{ modelValue: TField }> | null = null;

    private detailEditorProps: any = {};

    private validator: ValidatorDef | null = null;

    private name: string = '';

    private translationKey: string = '';

    private groupDef: GroupDef | null = null;

    private tabDef: TabDefinition | null = null;

    private collapsableDef: CollapsableDef | null = null;

    public constructor(name: string, translationKey: string) {
        this.name = name;
        this.translationKey = translationKey;
    }

    public Build = () => {
        const fieldDef = new FieldDef<T, TField>();
        fieldDef.DetailEditor = this.detailEditor!;
        fieldDef.DetailEditorProps = this.detailEditorProps;
        fieldDef.Validator = this.validator;
        fieldDef.Name = this.name;
        fieldDef.TranslationKey = this.translationKey;
        fieldDef.Group = this.groupDef;
        fieldDef.Tab = this.tabDef;
        fieldDef.Collapsable = this.collapsableDef;

        fieldDef.value = (obj: T) => {
            return (obj as any)[this.name] as TField;
        };
        return fieldDef;
    };

    /**
     * Attach it to the specified group
     * @param name
     * @param separator
     * @param alwaysShowLabel
     * @returns
     */
    public Group(name: string, separator: string | null, alwaysShowLabel: boolean | null = null) {
        this.groupDef = new GroupDef(name, separator, alwaysShowLabel);
        return this;
    }

    /**
     * Attach it to a tab definition
     * @param name
     * @param translationKey
     * @returns
     */
    public Tab(name: string, translationKey: string) {
        this.tabDef = new TabDefinition(name, translationKey);
        return this;
    }

    public Collapsable(titleKey: string, translationKey: string | null = null) {
        this.collapsableDef = new CollapsableDef(titleKey, translationKey);
        return this;
    }

    /**
     * Adds a cell renderer to the column
     * @param editor
     * @param params
     * @returns
     */
    public DetailEditor(editor: Component, params?: any) {
        this.detailEditor = markRaw(editor);
        this.detailEditorProps = params;
        return this;
    }
}
